// Section file — dash-core team. Sections: sidebar, dashboard, settings, magnet, booster, dailyBonus.
import React from "react";

const lang = {
  sidebar: {
    linkAccount: "Link Account",
    signedInAs: "Signed in as",
    upgradeYourAccount: "Upgrade Your Account",
    addAnotherProfile: "Add Another Profile",
    accountSettings: "Account Settings",
    billing: "Billing",
    logOut: "Log Out",
    upgradeNowToPro: "Upgrade Now To Pro",
    followerMagnet: "Follower Magnet",
    followerMagnetDesc: "Grow your followers automatically",
    dailyBonus: "Daily Bonus",
    boostTools: "Boost Tools",
    followers: "Followers",
    likes: "Likes",
    views: "Views",
    comments: "Comments",
    aiTools: "AI Tools",
    captionGenerator: "Caption Generator",
    ideaGenerator: "Idea Generator",
    profileEnhancer: "Profile Enhancer",
    trendsAnalyzer: "Trends Analyzer",
    photoReviewer: "Photo Reviewer",
    instagramTools: "Instagram Tools",
    collabFinder: "Collab Finder",
    storyViewer: "Story Viewer",
    postDownloader: "Post Downloader",
    charts: "Charts",
    trendingReels: "Trending Reels",
    trendingTiktoks: "Trending TikToks",
    trendingAudio: "Trending Audio",
  },
  dashboard: {
    linkAccount: "Link Account",
    setPassword: "Set Password",
    enterPasswordPlaceholder: "Enter password",
    updatePassword: "Update Password",
    pleaseEnterPassword: "Please enter a password",
	passwordReminderTitle: "Protect your account.",
	passwordReminderBody: "Set a password now so you can sign in with your email anytime.",
	passwordReminderAction: "Set password",
    loading: "Loading...",
    signedInAs: "Signed in as",
    dashboard: "Dashboard",
    upgradeYourAccount: "Upgrade Your Account",
    addAnotherProfile: "Add Another Profile",
    accountSettings: "Account Settings",
    billing: "Billing",
    logOut: "Log Out",
    noAccessToken: "No access token found. Please log in.",
    failedFetchProfile: "Failed to fetch profile info.",
    unableLoadProfile: "Unable to load profile. Please try again later.",
    tokenInvalid: "Token is invalid or expired.",
    sessionExpired: "Session expired. Please log in again.",
  },
  settings: {
	passwordSetupHint: "You do not have a password yet. Set one now to make future sign-ins easier.",
    // Function feedback messages
    switchedToMonthly: "Your plan has been switched to monthly billing! You will no longer be charged the annual rate.",
    errorSwitchingMonthly: "Error switching to monthly. Please contact support.",
    couldNotFindTransaction: "Could not find original transaction. Please contact support.",
    refundedAndSwitched: (amount, monthly, date) => `Done! You've been refunded $${amount} and switched to $${monthly}/month. Next charge: ${date}`,
    errorProcessingSwitch: "Error processing switch. Please contact support@viralgpt.com.",
    passwordUpdated: "Your password has been updated",
    passwordNotUpdated: "Your password has not been updated. Please check your old password and try again.",
    errorUpdatingPassword: "Error updating your password",
    passwordsDoNotMatch: "Passwords do not match!",
    profileUpdated: "Your profile settings were updated",
    errorUpdatingProfile: "Error updating your profile",
    billingUpdated: "Your billing information has been updated",
    subscriptionCancelled: "Your subscription has been cancelled. You will no longer be charged.",
    linkedCardChanged: "Your linked card has been changed",
    errorLinkingCard: "Error linking card",
    cardDeleted: "Your card has been deleted",
    errorDeletingCard: "Error deleting card",
    provideReason: "Please provide a reason for cancelling (minimum 15 characters)",
    subscriptionPaused: "Your subscription has been paused",
    errorPausingSubscription: "Error pausing subscription",
    subscriptionResumed: "Your subscription has been resumed",
    errorResumingSubscription: "Error resuming subscription",
    invoicePaid: "Your invoice has been paid",
    errorPayingInvoice: "Error paying invoice",
    accountUnlinked: "Your account has been unlinked",
    errorUpdatingGrowth: "Error updating growth status",
    freeBalanceAdded: "$50 has been added to your account balance!",
    refillSubmitted: (n) => `Refill request submitted successfully! ${n} followers will be delivered within 24 hours.`,
    errorSubmittingRefill: "Error submitting refill request",
    scannedMissing: (n) => `Your profile has been scanned. Missing followers: ${n}`,
    scannedNoMissing: "Your profile has been scanned. No missing followers found!",
    refillNotYet: "Your recent order is still settling — check back in a few days and we'll top you up free if followers are still short.",
    noFollowersDelivered: "No followers delivered yet for this profile.",
    // Page chrome
    title: "Settings",
    subtitle: "Manage your account, billing, and subscriptions.",
    tabs: { profile: "Account", billing: "Billing", invoices: "Invoices", refills: "Refills" },
    // Account tab
    accountInformation: "Account Information",
    accountDeleted: "Account deleted",
    emailAddress: "Email Address",
    enterYourEmail: "Enter your email",
    preferredLanguage: "Preferred Language",
    saveChanges: "Save Changes",
    changePassword: "Change Password",
    linkedProfiles: "Linked Profiles",
    unlink: "Unlink",
    addAnotherProfile: "Add another profile",
    // Billing tab
    paymentMethods: "Payment Methods",
    addCard: "Add Card",
    expires: (exp) => `Expires ${exp}`,
    linkedToActiveSub: "Linked to active subscription",
    deleteCard: "Delete card",
    cardNumber: "Card Number",
    expiration: "Expiration",
    cvc: "CVC",
    // Auto-refill plan. Its own card in the Billing tab, with a cancel that
    // takes one click — see the note in Settings.js.
    autoRefillTitle: "Auto-refill",
    autoRefillEvery: (d) => `every ${d} days`,
    autoRefillNext: (date) => `Next charge ${date}`,
    autoRefillActive: "Active",
    autoRefillCanceled: "Canceled — your followers stay on your account",
    autoRefillCancel: "Cancel auto-refill",
    autoRefillResume: "Resume",
    autoRefillConfirm: "Cancel auto-refill? You keep every follower already delivered, and you will not be billed again.",
    autoRefillCanceledOk: "Auto-refill canceled. You will not be billed again.",
    autoRefillResumedOk: "Auto-refill resumed.",
    autoRefillError: "Could not update auto-refill. Please try again.",
    subscriptions: "Subscriptions",
    cancelled: "CANCELLED",
    inactive: "INACTIVE",
    noPlan: "NO PLAN",
    growthPaused: "GROWTH PAUSED",
    expiresIn: "Expires in",
    daysRemaining: "Days remaining",
    daysCount: (n) => `${n} days`,
    nextCharge: "Next charge",
    interval: "Interval",
    started: "Started",
    na: "N/A",
    payInvoice: "Pay Invoice",
    selectCard: "Select card",
    upgradeToPro: "Upgrade to Pro",
    resumeGrowth: "Resume Growth",
    pauseGrowth: "Pause Growth",
    cancel: "Cancel",
    resume: "Resume",
    purchasePlan: "Purchase Plan",
    // Invoices tab
    invoiceHistory: "Invoice History",
    invoiceTh: "Invoice",
    profileTh: "Profile",
    amountTh: "Amount",
    dateTh: "Date",
    statusTh: "Status",
    payNow: "Pay Now",
    noInvoicesFound: "No invoices found",
    invoiceNum: (id) => `Invoice #${id}`,
    // Refills tab
    requestRefillTitle: "Request a Follower Refill",
    refillDesc1: "Request a refill if you've lost followers during your subscription period.",
    refillDesc2: "This is not for refilling instant boosts.",
    selectProfile: "Select Profile",
    chooseProfile: "Choose a profile...",
    scanning: "Scanning...",
    scanProfile: "Scan Profile",
    scanResults: "Scan Results",
    noMissingFollowers: "No missing followers!",
    current: "Current",
    gainedVia: "Gained via ViralGPT",
    expected: "Expected",
    missing: "Missing",
    missingCount: (n) => `Missing ${n} followers`,
    submitting: "Submitting...",
    requestFollowerRefill: "Request Follower Refill",
    // Pay Invoice modal
    payInvoiceTitle: "Pay Invoice",
    reactivateNote: "Your subscription will reactivate from today and be valid for the next 30 days",
    paymentMethod: "Payment Method",
    addNewCard: "Add New Card",
    cardNumberPh: "Card Number",
    cvvPh: "CVV",
    processing: "Processing...",
    payAmount: (p) => `Pay $${p}`,
    // Add Card / Link / Unlink modals
    addPaymentMethod: "Add Payment Method",
    linkAccount: "Link Account",
    unlinkAccount: "Unlink Account",
    unlinkConfirm: (name) => <>Are you sure you want to unlink <strong style={{color: '#0a2540'}}>{name}</strong>? This action cannot be undone.</>,
    unlinkProfileBtn: "Unlink Profile",
    // Cancel subscription modal
    cancelSubscription: "Cancel Subscription",
    cancelWhy: "Please tell us why you're cancelling. This helps us improve our service.",
    reason: "Reason",
    selectReason: "Select a reason...",
    reasonMissingFollowers: "Missing Followers",
    reasonPriceTooHigh: "Price is too high",
    reasonAccidentalAnnual: "Accidentally purchased annual",
    reasonSwitchMonthly: "I'd rather pay monthly",
    reasonOther: "Other",
    refillOffer: "We can refill your missing followers for free!",
    goToRefills: "Go to Refills",
    bonusOffer: "We'd like to offer you a one-time free bonus!",
    getFreeBalance: "Get $50 Free Balance",
    switchMonthlyOffer: (price) => `Or switch to monthly billing instead — pay just $${price}/month with no annual commitment.`,
    switching: "Switching...",
    switchToMonthly: "Switch to Monthly",
    switchRefundTitle: "Switch to Monthly + Get Refunded",
    refundExplain: (diff, annual, monthly) => <>
      We'll refund <strong>${diff}</strong> to your card (the difference between ${annual} annual and ${monthly}/month) and switch you to monthly billing.
    </>,
    annualChargePaid: "Annual charge paid",
    newMonthlyRate: "New monthly rate",
    perMo: (p) => `$${p}/mo`,
    refundToCard: "Refund to your card",
    refundAndSwitch: (d) => `Refund $${d} & Switch to Monthly`,
    switchToMonthlyBilling: "Switch to Monthly Billing",
    keepPlanOffer: (plan, price) => <>
      Instead of cancelling, keep your {plan} plan at just <strong>${price}/month</strong>. No more annual charges — cancel anytime.
    </>,
    details: "Details",
    detailsPlaceholder: "Please provide at least 15 characters...",
    provide15: "Please provide at least 15 characters.",
    charsMin: "15 characters minimum",
    keepSubscription: "Keep Subscription",
    close: "Close",
    // Change password modal
    currentPassword: "Current Password",
    enterCurrentPassword: "Enter current password",
    newPassword: "New Password",
    enterNewPassword: "Enter new password",
    confirmNewPassword: "Confirm New Password",
    confirmNewPasswordPh: "Confirm new password",
    savePassword: "Save Password",
  },
  magnet: {
    growthPausedMsg: "Growth paused. You can resume anytime.",
    growthResumedMsg: "Growth resumed! Followers will begin delivering within 24 hours.",
    failedUpdateGrowth: "Failed to update growth status.",
    profileRefreshed: "Profile refreshed successfully.",
    failedRefreshProfile: "Failed to refresh profile.",
    failedSaveSettings: (err) => `Failed to save settings: ${err}`,
    addAccount: "Add Account",
    comparePlans: "Compare Plans",
    manageAddons: "Manage Add-ons",
    outstandingInvoice: "You have an outstanding invoice for this month.",
    payYourInvoice: "Please pay your invoice",
    toContinue: "to continue using our services.",
    tabMagnet: "Magnet",
    tabExtraBoosts: "Instant Boosts",
    tabAiTools: "AI Tools",
    learningPhase: "Learning Phase:",
    learningPhaseStart: "Follower delivery will begin within 24 hours.",
    learningPhaseTuning: "Our AI is fine-tuning your targeting so you attract higher-quality, more engaged followers who actually fit your niche.",
    gained24h: "Followers gained in the last 24 hours",
    gained7d: "Followers gained in the last 7 days",
    gained30d: "Followers gained in the last 30 days",
    refillPrompt: "Missing followers? Request a free refill",
    here: "here",
    statusNoAccount: "No Account Linked",
    statusPausedPrivate: "Paused - Private Account",
    statusGrowthPaused: "Growth Paused",
    statusActive: "ACTIVE",
    noPlanBadge: "NO PLAN",
    noPlan: "No Plan",
    activeBadge: "Active",
    noActiveSubscription: "No active subscription",
    ratePerDay: (rate, unit) => `${rate} ${unit} / day`,
    subs: "subs",
    subscribers: "subscribers",
    followersWord: "followers",
    startGrowing: "Start Growing",
    upgradePlan: "Upgrade Plan",
    boostGrowth: "Boost Growth",
    updating: "Updating...",
    resumeGrowthLink: "Resume growth",
    pauseGrowthLink: "Pause growth",
    projectedGrowth: "Projected Growth",
    projection: (active, count, unit, time) => <>Your account {active ? 'will' : 'could'} reach <strong style={{color: '#0a2540'}}>{count}</strong> {unit} in {time}.</>,
    monthsCount: (n) => `${n} months`,
    daysCount: (n) => `${n} days`,
    targetInterests: "Target Interests",
    targetInterestsDesc: "ViralGPT uses AI to recognize the user's interests from their bio & posts.",
    targetCountries: "Target Countries",
    targetCountriesDesc: (unit) => `ViralGPT will only target ${unit} from the selected countries.`,
    targetDemographics: "Target Demographics",
    targetDemoDesc: (unit) => `ViralGPT will target more people within the selected age range and gender.`,
    yearsRange: (a, b) => `${a} - ${b} years`,
    genderMale: "Male",
    genderFemale: "Female",
    genderBoth: "Both",
    maleDesc: (unit) => `Target more male ${unit}`,
    femaleDesc: (unit) => `Target more female ${unit}`,
    bothDesc: "Target all genders",
    pauseConfirmTitle: "Pause growth?",
    pauseConfirmBody: "Follower delivery will stop until you resume. You can resume anytime from your dashboard.",
    cancel: "Cancel",
    yesPause: "Yes, Pause",
    interests: {
      global: "Everyone",
      fitness: "Fitness",
      travel: "Travel",
      fashion: "Fashion",
      tech: "Technology",
      food: "Food",
      music: "Music",
      art: "Art",
      gaming: "Gaming",
      photography: "Photography",
      sports: "Sports",
      health: "Health",
      beauty: "Beauty",
      books: "Books",
      movies: "Movies",
      pets: "Pets",
      business: "Business",
      realestate: "Real Estate",
      homedecor: "Home Decor",
      education: "Education",
      development: "Personal Development",
      wellness: "Health & Wellness",
      memes: "Memes/Humor",
      nature: "Nature",
      cars: "Cars",
      writing: "Writing",
      news: "News",
      science: "Science",
      politics: "Politics",
      history: "History",
    },
    countries: {
      global: "Global",
      us: "United States",
      ca: "Canada",
      mx: "Mexico",
      gb: "United Kingdom",
      au: "Australia",
      nz: "New Zealand",
      de: "Germany",
      fr: "France",
      es: "Spain",
      it: "Italy",
      nl: "Netherlands",
      be: "Belgium",
      se: "Sweden",
      no: "Norway",
      dk: "Denmark",
      ch: "Switzerland",
      at: "Austria",
      pl: "Poland",
      pt: "Portugal",
      gr: "Greece",
      ro: "Romania",
      bg: "Bulgaria",
      hu: "Hungary",
      cz: "Czechia",
      br: "Brazil",
      ar: "Argentina",
      cl: "Chile",
      co: "Colombia",
      pe: "Peru",
      ve: "Venezuela",
      ec: "Ecuador",
      uy: "Uruguay",
      py: "Paraguay",
      bo: "Bolivia",
      cr: "Costa Rica",
      pa: "Panama",
      do: "Dominican Republic",
      gt: "Guatemala",
      sv: "El Salvador",
      hn: "Honduras",
      ni: "Nicaragua",
    },
  },
  booster: {
    // The remembered configuration, recapped under a chosen quantity. These are
    // read as L.booster.* — they belong to the Instant Boosts panel, not the
    // settings page.
    editOptions: "Edit options",
    buyNowAmount: (p) => `Buy Now — $${p}`,
    anyGender: "Any gender",
    optQuality: "Quality",
    optCountry: "Country",
    optGender: "Gender",
    optSpeed: "Speed",
    confirmTitle: "Confirm your boost",
    payNow: (t) => `Pay $${t}`,
    deliverTo: (handle) => `Deliver to @${handle}`,
    changeAccount: "Change account",
    // The handle is collected in a popup opened by the CTA, so the button names
    // the step it opens rather than promising a purchase it can't complete yet.
    chooseProfile: "Choose profile",
    profileModalTitle: "Which account is this for?",
    // X/YouTube likes and views name a post, not an account — the popup's
    // question tracks linkLabel's branch so it never asks for the wrong thing.
    postModalTitle: "Which post is this for?",
    profileLookingUp: "Looking up account...",
    profileNotFound: "We couldn't find that account — double-check the handle.",
    profileModalContinue: "Continue",
    useThisAccount: "Use this account",
    loadPosts: "Choose posts",
    loadingPosts: "Loading posts...",
    serviceLabel: "Service",
    postsChosen: (n, max) => `${n} of ${max} posts selected — the amount is split evenly between them.`,
    tierLabelService: (qty, kind) => `${qty} ${kind}`,
    bundleSpread: "Delivery",
    recentPosts: "Recent posts",
    engagementBundles: "Engagement bundles",
    bundleLabel: (l, v) => `${l} likes + ${v} views`,
    bundleSub: (p) => `Spread across your recent ${p} posts`,
    bundleQueued: "Your engagement bundle is on its way.",
    bundleFailed: "Could not place that bundle. Please try again.",
    addonTitle: "Improve this boost",
    addonSub: "Optional extras applied to your delivery.",
    addonFailed: "Could not apply those extras.",
    addons: { fast: { title: "Super Instant Delivery", sub: "Delivery starts in seconds." }, female: { title: "Female followers only", sub: "We target women exclusively" }, usa: { title: "USA followers only", sub: "We target United States accounts" } },
    quickSelect: "Choose an amount",
    customAmount: "Custom amount",
    prefDelivery: "Delivery",
    prefQuality: "Quality",
		baseFollowers: "Base followers",
		upsellLabels: { engagement: "Extra Followers", views: "Boost Engagement+", likes: "Auto Likes" },
    prefTargeting: "Audience",
    prefLocation: "Location",
    postsBoosted: (n) => `${n} ${n === 1 ? "post" : "posts"} boosted`,
    viewDetails: "Details",
    // A paid engagement pack that couldn't be delivered because the account was
    // private when it was bought. The panel can't see a private account's posts.
    packNeedsPublic: "We couldn't deliver this yet — your account has to be public for us to reach your posts. Set it to public, then press Send.",
    packBadUsername: "We couldn't find that account. Check the spelling and enter your username — not your display name.",
    packUsernamePrompt: "We couldn't find this account",
    packUsernameHelp: "Enter your username (not your display name) and we'll send this order.",
    packUsernamePlaceholder: "your.username",
    packUsernameSave: "Save & Send",
    packUsernameMalformed: "That doesn't look like a username. Use only letters, numbers, dots, dashes and underscores — no spaces.",
    packUsernameRequired: "Enter a username so we know where to send this order.",
    packCheckUnavailable: "We couldn't verify that account just now. Please try again in a minute.",
    // The button names what it sends. Resendable orders are mostly likes and
    // views, not followers, so a single "Send Followers" would misdescribe the
    // order on most rows. Keyed by the service segment of boost_type.
    packSendBy: {
      followers: "Send Followers",
      likes: "Send Likes",
      views: "Send Views",
      comments: "Send Comments",
      engagement: "Send Engagement",
    },
    packSend: "Send Order",
    packSending: "Sending...",
    // Shown at the top of the dashboard when a paid order is sitting undelivered
    // waiting on the customer to make their account public.
    pendingSendTitle: (n) => (n === 1 ? "You have 1 order waiting to be sent" : `You have ${n} orders waiting to be sent`),
    pendingSendBody: "Your account has to be public for us to reach your posts. Set it to public, then press Send.",
    pendingSendCta: "Go to order history",
		deliveryInProgressTitle: (order) => `Your ${order} are being delivered`,
		deliveryStatus: (status) => `Status: ${status}`,
		deliveryStarting: "Starting",
		deliveryInProgress: "Delivery in progress",
    packStillPrivate: "Your account still looks private. Set it to public, then try again.",
    packSendFailed: "Couldn't send that just now. Please try again.",
    orderDetails: "Order Details",
    thAccount: "Account",
    addFundsTitle: "Add funds",
    buyNow: "Buy now",
    addFundsBonus: (n) => `Add funds — get $${n} bonus`,
    payingWithBalance: "Paying with balance",
    paidAtCheckout: "Paid",
    waitPrevious: "Please wait for the previous order to complete before starting a new one.",
    startingDelivery: (type, amount, link, price) => `Starting delivery for ${type}!\nAmount: ${amount}\nPost Link: ${link}\nCharge: $${price}`,
    failedCreateOrder: (err) => `Failed to create order: ${err}`,
    noPosts: "Could not find any posts. Please check if the username is correct or if the account is private.",
    services: { followers: "Followers", likes: "Likes", views: "Views", comments: "Comments" },
    platformLabel: "Platform",
    enterUsername: (p) => `Enter ${p} username here`,
    enterUsernameDefault: "Enter username",
    youtubeProfileLink: "YouTube Profile Link",
    usernameLabel: (p) => `${p} Username`,
    postLinkLabel: (p) => `${p} Post Link`,
    followersAmountMin: (min) => `Followers Amount (Min: ${min})`,
    likesAmount: "Likes Amount",
    viewsAmount: "Views Amount",
    customComments: "Custom Comments (1 per line)",
    commentsPlaceholder: "Enter custom comments here\nOne comment per line",
    selectAPost: "Select a Post",
    pageN: (n) => `Page ${n}`,
    selectPostContinue: "Select a post above to continue",
    orderSummary: "Order Summary",
    pricePer: (u) => `Price per ${u}`,
    quantity: "Quantity",
    subtotal: "Subtotal",
    proDiscount: "Pro Discount",
    total: "Total",
    processing: "Processing...",
    serviceUnavailable: "Service Unavailable",
    choosePost: "Choose a Post",
    startBoost: "Start Boost",
    instant: "Instant",
    noPassword: "No Password",
    avgDeliveryTime: "Average delivery time",
    orderHistory: "Order History",
    noOrders: "No orders yet",
    thService: "Service",
    thLink: "Link",
    thCharge: "Charge",
    thStart: "Start",
    thAmount: "Amount",
    thRemains: "Remains",
    thStatus: "Status",
    thDate: "Date",
    qty: (v) => `Qty: ${v}`,
  },
  dailyBonus: {
    celebrations: {
      common: ["Bonus Claimed", "Nice Win!", "Daily Bonus", "Nice Hit!", "You Won!"],
      rare: ["Great Spin!", "Big Bonus!", "Solid Hit!", "Lucky Reward!"],
      epic: ["Massive Win!", "Insane Hit!", "Huge Reward!", "Mega Bonus!"],
      legendary: ["JACKPOT!", "Massive Win!", "Legendary Spin!", "Huge Reward!"],
    },
    loading: "Loading…",
    heading: "Daily Bonus Spin",
    sub: "Spin the wheel — $ added straight to your balance ✨",
    spinAvailable: "Spin Available",
    spinsAvailable: "Spins Available",
    free: "FREE",
    dailySpinReady: "Daily Spin Ready",
    nextFreeSpin: "Next free spin tomorrow",
    lockedTitle: "Daily free spin locked",
    lockedSub: "Get a free spin every day on Basic or Pro.",
    upgrade: "Upgrade",
    spin: "SPIN",
    earnedThisMonth: "Earned this month",
    oneExtraSpin: "1 Extra Spin",
    buyNow: "Buy Now",
    saveAmount: (n) => `Save $${n}`,
    fiveSpinsPack: "5 Spins Pack",
    buyPack: "Buy Pack",
    addedToBalance: "added to your balance",
    continue: "Continue",
  },

  // Dashboard membership offer. Claims only what a membership actually
  // delivers — monthly balance and delivery priority. No follower promises, no
  // anchor pricing, and dashboard access is not listed: they already have it.
  membership: {
    heading: "Become a member",
    subheading: "Members get balance added every month for less than it costs to buy.",
    perMonth: "/month",
    bestValue: "Best Value",
    balanceEveryMonth: (amount) => `Get $${amount} added to your balance every month`,
    effectiveDiscount: (pct) => `${pct}% off every instant boost`,
    plans: {
      growth: {
        name: "Growth",
        cta: "Join Growth",
        features: [
          "Spend balance on any instant boost",
          "Priority delivery",
          "Member-only top-up bonuses",
          "Cancel anytime",
        ],
      },
      expert: {
        name: "Expert",
        cta: "Join Expert",
        features: [
          "Everything in Growth",
          "Better value per dollar",
          "Highest-priority delivery",
          "Premium support",
          "Cancel anytime",
        ],
      },
    },
  },
};

export default lang;
